import SnapType from './snapType';
import LineOrientation from './lineOrientation';
import BaseCanvasPositioningHelper from './baseCanvasPositioningHelper';
import { FAMILY_PREVIEW_EDITOR_CONFIG as CONFIG } from '@/constants/familyPreviewEditor';

class CanvasProductPositioningHelper extends BaseCanvasPositioningHelper {
  constructor() {
    super();
  }

  getAlignmentLines(productNode, stage) {
    const possibleAlignmentLines = this.getPossibleAlignmentLines(productNode, stage);
    const productBounds = this.getNodeSnappingEdges(productNode);

    return this.getClosestAlignmentLines(possibleAlignmentLines, productBounds, CONFIG.canvasPositioningHelper.product.minimalStepSize);
  };

  getPossibleAlignmentLines(currentProductNode, stage) {
    const vertical = [];
    const horizontal = [];

    stage.find(`.${CONFIG.global.product.nodeName}`).forEach(product => {
      if (product === currentProductNode) {
        return;
      }

      var rect = product.getClientRect();

      vertical.push([rect.x, rect.x + rect.width, rect.x + rect.width / 2]);
      horizontal.push([rect.y, rect.y + rect.height, rect.y + rect.height / 2]);
    });

    return {
      vertical: vertical.flat(),
      horizontal: horizontal.flat()
    };
  };

  getEqualDistanceAlignment(productNode, clustersProducts) {
    const cluster = this.getProductCluster(clustersProducts, productNode);

    if (!cluster) {
      return {
        equalDistanceIndicators: [],
        alignmentLines: []
      };
    }

    const betweenProductsAlignment = this.getEqualDistanceAlignmentBetweenProducts(clustersProducts, productNode, cluster);
    const toClusterBordersAlignment = this.getEqualDistanceAlignmentToClusterBorders(clustersProducts, productNode);

    return {
      equalDistanceIndicators: betweenProductsAlignment.equalDistanceIndicators.concat(toClusterBordersAlignment.equalDistanceIndicators),
      alignmentLines: betweenProductsAlignment.alignmentLines.concat([])//.concat([])//toClusterBordersAlignment.alignmentLines
    };
  };

  getEqualDistanceAlignmentBetweenProducts(clustersProducts, currentProduct, currentCluster) {
    const minVerticalDistanceData = this.getMinimalVerticalDistanceWithIndicatorsBetweenProducts(clustersProducts, currentProduct);
    const minHorizontalDistanceData = this.getMinimalHorizontalDistanceWithIndicatorsBetweenProducts(clustersProducts, currentProduct);

    const alignment = {
      equalDistanceIndicators: minVerticalDistanceData.equalDistanceIndicators.concat(minHorizontalDistanceData.equalDistanceIndicators),
      alignmentLines: []
    };

    const minStepSize = CONFIG.canvasPositioningHelper.product.minimalStepSize;

    const clusterProducts = this.getClusterProducts(clustersProducts, currentCluster);
    const currentProductRect = currentProduct.getClientRect();

    const currentProductCenterXCoord = Math.round(currentProductRect.x + (currentProductRect.width / 2));
    const currentProductCenterYCoord = Math.round(currentProductRect.y + (currentProductRect.height / 2));

    for (const product of clusterProducts) {
      if (product === currentProduct) {
        continue;
      }

      const productRect = product.getClientRect();

      if (!this.isVerticalIntersection(currentProductRect, productRect)) {
        if (currentProductRect.y > productRect.y) {
          // Comparable product is above the current
          const distance = currentProductRect.y - (productRect.y + productRect.height);

          if (Math.round(distance - minVerticalDistanceData.minDistance) <= minStepSize) {
            alignment.alignmentLines.push({
              line: productRect.y + productRect.height + minVerticalDistanceData.minDistance,
              coordinates: {
                fromCoordX: currentProductRect.x,
                fromCoordY: productRect.y + productRect.height + minVerticalDistanceData.minDistance,
                toCoordX: currentProductRect.x + currentProductRect.width,
                toCoordY: productRect.y + productRect.height + minVerticalDistanceData.minDistance
              },
              offset: Math.abs(distance - minVerticalDistanceData.minDistance),
              orientation: LineOrientation.Horizontal,
              snap: SnapType.Disabled
            });

            alignment.equalDistanceIndicators.push({
              fromCoordX: currentProductCenterXCoord,
              fromCoordY: productRect.y + productRect.height,
              toCoordX: currentProductCenterXCoord,
              toCoordY: productRect.y + productRect.height + minVerticalDistanceData.minDistance,
              orientation: LineOrientation.Vertical
            });
          }
        } else {
          // Comparable product is under current
          const distance = productRect.y - (currentProductRect.y + currentProductRect.height);

          if (Math.round(distance - minVerticalDistanceData.minDistance) <= minStepSize) {
            alignment.alignmentLines.push({
              line: productRect.y - minVerticalDistanceData.minDistance,
              coordinates: {
                fromCoordX: currentProductRect.x,
                fromCoordY: productRect.y - minVerticalDistanceData.minDistance,
                toCoordX: currentProductRect.x + currentProductRect.width,
                toCoordY: productRect.y - minVerticalDistanceData.minDistance
              },
              offset: Math.abs(distance - minVerticalDistanceData.minDistance),
              orientation: LineOrientation.Horizontal,
              snap: SnapType.Disabled
            });

            alignment.equalDistanceIndicators.push({
              fromCoordX: currentProductCenterXCoord,
              fromCoordY: productRect.y - minVerticalDistanceData.minDistance,
              toCoordX: currentProductCenterXCoord,
              toCoordY: productRect.y,
              orientation: LineOrientation.Vertical
            });
          }
        }
      }

      if (!this.isHorizontalIntersection(currentProductRect, productRect)) {
        if (currentProductRect.x > productRect.x) {
          // Comparable product is to the left of current
          const distance = currentProductRect.x - (productRect.x + productRect.width);

          if (Math.round(distance - minHorizontalDistanceData.minDistance) <= minStepSize) {
            alignment.alignmentLines.push({
              line: productRect.x + productRect.width + minHorizontalDistanceData.minDistance,
              coordinates: {
                fromCoordX: productRect.x + productRect.width + minHorizontalDistanceData.minDistance,
                fromCoordY: currentProductRect.y,
                toCoordX: productRect.x + productRect.width + minHorizontalDistanceData.minDistance,
                toCoordY: currentProductRect.y + currentProductRect.height
              },
              offset: Math.abs(distance - minHorizontalDistanceData.minDistance),
              orientation: LineOrientation.Vertical,
              snap: SnapType.Disabled
            });

            alignment.equalDistanceIndicators.push({
              fromCoordX: productRect.x + productRect.width,
              fromCoordY: currentProductCenterYCoord,
              toCoordX: productRect.x + productRect.width + minHorizontalDistanceData.minDistance,
              toCoordY: currentProductCenterYCoord,
              orientation: LineOrientation.Horizontal
            });
          }
        } else {
          // Comparable product is to the right of current
          const distance = productRect.x - (currentProductRect.x + currentProductRect.width);

          if (Math.round(distance - minHorizontalDistanceData.minDistance) <= minStepSize) {
            alignment.alignmentLines.push({
              line: productRect.x - minHorizontalDistanceData.minDistance,
              coordinates: {
                fromCoordX: productRect.x - minHorizontalDistanceData.minDistance,
                fromCoordY: currentProductRect.y,
                toCoordX: productRect.x - minHorizontalDistanceData.minDistance,
                toCoordY: currentProductRect.y + currentProductRect.height
              },
              offset: Math.abs(distance - minHorizontalDistanceData.minDistance),
              orientation: LineOrientation.Vertical,
              snap: SnapType.Disabled
            });

          alignment.equalDistanceIndicators.push({
            fromCoordX: productRect.x - minHorizontalDistanceData.minDistance,
            fromCoordY: currentProductCenterYCoord,
            toCoordX: productRect.x,
            toCoordY: currentProductCenterYCoord,
            orientation: LineOrientation.Horizontal
          });
        }
        }
      }
    }

    return alignment;
  };

  getMinimalVerticalDistanceWithIndicatorsBetweenProducts(clustersProducts, currentProduct) {
    let minDistance = CONFIG.canvasPositioningHelper.product.minDistance;
    let equalDistanceIndicators = [];

    for (const clusterId in clustersProducts) {
      const products = clustersProducts[clusterId].products;

      for (let i = 0; i < products.length; i++) {
        if (products[i] === currentProduct) {
          continue;
        }

        const iRectangle = products[i].getClientRect();

        for (let j = i + 1; j < products.length; j++) {
          if (products[j] === currentProduct) {
            continue;
          }

          const jRectangle = products[j].getClientRect();

          if (this.isVerticalIntersection(iRectangle, jRectangle)) {
            continue;
          }

          const distance = this.getVerticalDistanceBetweenProducts(iRectangle, jRectangle);

          if (minDistance !== undefined && distance < minDistance) {
            equalDistanceIndicators = [];
          }

          if (minDistance === undefined || distance < minDistance) {
            minDistance = distance;

            equalDistanceIndicators.push({
              fromCoordX: iRectangle.y > jRectangle.y
                ? iRectangle.x + (iRectangle.width / 2)
                : jRectangle.x + (jRectangle.width / 2),
              fromCoordY: iRectangle.y > jRectangle.y
                ? iRectangle.y + iRectangle.height
                : jRectangle.y + jRectangle.height,
              toCoordX: iRectangle.y > jRectangle.y
              ? iRectangle.x + (iRectangle.width / 2)
              : jRectangle.x + (jRectangle.width / 2),
              toCoordY: iRectangle.y > jRectangle.y
                ? jRectangle.y
                : iRectangle.y,
              orientation: LineOrientation.Vertical
            });
          }
        }
      }
    }

    return {
      minDistance: minDistance,
      equalDistanceIndicators: equalDistanceIndicators
    };
  };

  getMinimalHorizontalDistanceWithIndicatorsBetweenProducts(clustersProducts, currentProduct) {
    let minDistance = undefined;
    let equalDistanceIndicators = [];

    for (const clusterId in clustersProducts) {
      const products = clustersProducts[clusterId].products;

      for (let i = 0; i < products.length; i++) {
        if (products[i] === currentProduct) {
          continue;
        }

        const iRectangle = products[i].getClientRect();

        for (let j = i + 1; j < products.length; j++) {
          if (products[j] === currentProduct) {
            continue;
          }

          const jRectangle = products[j].getClientRect();

          if (this.isHorizontalIntersection(iRectangle, jRectangle)) {
            continue;
          }

          const distance = this.getHorizontalDistanceBetweenProducts(iRectangle, jRectangle);

          if (minDistance !== undefined && distance < minDistance) {
            equalDistanceIndicators = [];
          }

          if (minDistance === undefined || distance < minDistance) {
            minDistance = distance;

            equalDistanceIndicators.push({
              fromCoordX: iRectangle.x > jRectangle.x
                ? jRectangle.x + jRectangle.width
                : iRectangle.x + iRectangle.width,
              fromCoordY: iRectangle.x > jRectangle.x
                ? jRectangle.y + (jRectangle.height / 2)
                : iRectangle.y + (iRectangle.height / 2),
              toCoordX: iRectangle.x > jRectangle.x
                ? iRectangle.x
                : jRectangle.x,
              toCoordY: iRectangle.x > jRectangle.x
                ? jRectangle.y + (jRectangle.height / 2)
                : iRectangle.y + (iRectangle.height / 2),
              orientation: LineOrientation.Horizontal
            });
          }
        }
      }
    }

    return {
      minDistance: minDistance,
      equalDistanceIndicators: equalDistanceIndicators
    };
  };

  getEqualDistanceAlignmentToClusterBorders(clustersProducts, currentProduct) {
    const alignment = {
      equalDistanceIndicators: [],
      alignmentLines: []
    };

    const minVerticalDistance = this.getMinimalVerticalDistanceToClusterBorder(clustersProducts, currentProduct);
    const minHorizontalDistance = this.getMinimalHorizontalDistanceToClusterBorder(clustersProducts, currentProduct);

    const minStepSize = CONFIG.canvasPositioningHelper.product.minimalStepSize;

    for (const clusterId in clustersProducts) {
      let clusterRectangle = clustersProducts[clusterId].cluster.find(`.${CONFIG.rectangle.nodeName}`)[0].getClientRect();
      const products = clustersProducts[clusterId].products;

      for (const product of products) {
        const productRectangle = product.getClientRect();

        let distanceToTopBorder = productRectangle.y - clusterRectangle.y;
        let distanceToBottomBorder = (clusterRectangle.y + clusterRectangle.height) - (productRectangle.y + productRectangle.height);
        let distanceToLeftBorder = productRectangle.x - clusterRectangle.x;
        let distanceToRightBorder = (clusterRectangle.x + clusterRectangle.width) - (productRectangle.x + productRectangle.width);

        const productCenterXCoord = Math.round(productRectangle.x + (productRectangle.width / 2));
        const productCenterYCoord = Math.round(productRectangle.y + (productRectangle.height / 2));

        if (product === currentProduct) {
          const currentProductCluster = this.getProductCluster(clustersProducts, currentProduct);

          if (currentProductCluster !== clustersProducts[clusterId].cluster) { // Detect if product moved to another cluster
            clusterRectangle = currentProductCluster.getClientRect();

            distanceToTopBorder = productRectangle.y - clusterRectangle.y;
            distanceToBottomBorder = (clusterRectangle.y + clusterRectangle.height) - (productRectangle.y + productRectangle.height);
            distanceToLeftBorder = productRectangle.x - clusterRectangle.x;
            distanceToRightBorder = (clusterRectangle.x + clusterRectangle.width) - (productRectangle.x + productRectangle.width);
          }

          if (Math.abs(distanceToTopBorder - minVerticalDistance) <= minStepSize) {
            alignment.alignmentLines.push({
              line: clusterRectangle.y + minVerticalDistance,
              coordinates: {
                fromCoordX: productRectangle.x,
                fromCoordY: clusterRectangle.y + minVerticalDistance,
                toCoordX: productRectangle.x + productRectangle.width,
                toCoordY: clusterRectangle.y + minVerticalDistance
              },
              offset: Math.abs(distanceToTopBorder - minVerticalDistance),
              orientation: LineOrientation.Horizontal,
              snap: SnapType.Start
            });

            alignment.equalDistanceIndicators.push({
              fromCoordX: productCenterXCoord,
              fromCoordY: clusterRectangle.y,
              toCoordX: productCenterXCoord,
              toCoordY: clusterRectangle.y + minVerticalDistance,
              orientation: LineOrientation.Vertical
            });
          }

          if (Math.abs(distanceToBottomBorder - minVerticalDistance) <= minStepSize) {
            alignment.alignmentLines.push({
              line: clusterRectangle.y + clusterRectangle.height - minVerticalDistance,
              coordinates: {
                fromCoordX: productRectangle.x,
                fromCoordY: clusterRectangle.y + clusterRectangle.height - minVerticalDistance,
                toCoordX: productRectangle.x + productRectangle.width,
                toCoordY: clusterRectangle.y + clusterRectangle.height - minVerticalDistance
              },
              offset: Math.abs(distanceToBottomBorder - minVerticalDistance),
              orientation: LineOrientation.Horizontal,
              snap: SnapType.End
            });

            alignment.equalDistanceIndicators.push({
              fromCoordX: productCenterXCoord,
              fromCoordY: clusterRectangle.y + clusterRectangle.height - minVerticalDistance,
              toCoordX: productCenterXCoord,
              toCoordY: clusterRectangle.y + clusterRectangle.height,
              orientation: LineOrientation.Vertical
            });
          }

          if (Math.abs(distanceToLeftBorder - minHorizontalDistance) <= minStepSize) {
            alignment.alignmentLines.push({
              line: clusterRectangle.x + minHorizontalDistance,
              coordinates: {
                fromCoordX: clusterRectangle.x + minHorizontalDistance,
                fromCoordY: productRectangle.y,
                toCoordX: clusterRectangle.x + minHorizontalDistance,
                toCoordY: productRectangle.y + productRectangle.height
              },
              offset: Math.abs(distanceToLeftBorder - minVerticalDistance),
              orientation: LineOrientation.Vertical,
              snap: SnapType.Start
            });
            
            alignment.equalDistanceIndicators.push({
              fromCoordX: clusterRectangle.x,
              fromCoordY: productRectangle.y + (productRectangle.height / 2),
              toCoordX: clusterRectangle.x + minHorizontalDistance,
              toCoordY: productRectangle.y + (productRectangle.height / 2),
              orientation: LineOrientation.Horizontal
            });
          }

          if (Math.abs(distanceToRightBorder - minHorizontalDistance) <= minStepSize) {
            alignment.alignmentLines.push({
              line: clusterRectangle.x + clusterRectangle.width - minHorizontalDistance,
              coordinates: {
                fromCoordX: clusterRectangle.x + clusterRectangle.width - minHorizontalDistance,
                fromCoordY: productRectangle.y,
                toCoordX: clusterRectangle.x + clusterRectangle.width - minHorizontalDistance,
                toCoordY: productRectangle.y + productRectangle.height
              },
              offset: Math.abs(distanceToRightBorder - minVerticalDistance),
              orientation: LineOrientation.Vertical,
              snap: SnapType.End
            });
            
            alignment.equalDistanceIndicators.push({
              fromCoordX: clusterRectangle.x + clusterRectangle.width - minHorizontalDistance,
              fromCoordY: productRectangle.y + (productRectangle.height / 2),
              toCoordX: clusterRectangle.x + clusterRectangle.width,
              toCoordY: productRectangle.y + (productRectangle.height / 2),
              orientation: LineOrientation.Horizontal
            });
          }

          continue;
        }
        
        if (Math.round(distanceToTopBorder) === minVerticalDistance) {
          alignment.equalDistanceIndicators.push({
            fromCoordX: productCenterXCoord,
            fromCoordY: clusterRectangle.y,
            toCoordX: productCenterXCoord,
            toCoordY: productRectangle.y,
            orientation: LineOrientation.Vertical
          });
        }

        if (Math.round(distanceToBottomBorder) === minVerticalDistance) {
          alignment.equalDistanceIndicators.push({
            fromCoordX: productCenterXCoord,
            fromCoordY: productRectangle.y + productRectangle.height,
            toCoordX: productCenterXCoord,
            toCoordY: clusterRectangle.y + clusterRectangle.height,
            orientation: LineOrientation.Vertical
          });
        }

        if (Math.round(distanceToLeftBorder) === minHorizontalDistance) {
          alignment.equalDistanceIndicators.push({
            fromCoordX: clusterRectangle.x,
            fromCoordY: productCenterYCoord,
            toCoordX: productRectangle.x,
            toCoordY: productCenterYCoord,
            orientation: LineOrientation.Horizontal
          });
        }

        if (Math.round(distanceToRightBorder) === minHorizontalDistance) {
          alignment.equalDistanceIndicators.push({
            fromCoordX: productRectangle.x + productRectangle.width,
            fromCoordY: productCenterYCoord,
            toCoordX: clusterRectangle.x + clusterRectangle.width,
            toCoordY: productCenterYCoord,
            orientation: LineOrientation.Horizontal
          });
        }
      }
    };

    return alignment;
  };

  getMinimalVerticalDistanceToClusterBorder(clustersProducts, currentProduct) {
    let minDistance = undefined;

    for (const clusterId in clustersProducts) {
      const clusterRectangle = clustersProducts[clusterId].cluster.find(`.${CONFIG.rectangle.nodeName}`)[0].getClientRect();
      const products = clustersProducts[clusterId].products;

      for (let i = 0; i < products.length; i++) {
        if (products[i] === currentProduct) {
          continue;
        }

        const productRectangle = products[i].getClientRect();

        const distanceToTopBorder = productRectangle.y - clusterRectangle.y;
        const distanceToBottomBorder = (clusterRectangle.y + clusterRectangle.height) - (productRectangle.y + productRectangle.height);
        
        minDistance = minDistance === undefined
          ? Math.min(distanceToTopBorder, distanceToBottomBorder)
          : Math.min(minDistance, Math.min(distanceToTopBorder, distanceToBottomBorder));
      }
    }

    return Math.round(minDistance);
  };

  getMinimalHorizontalDistanceToClusterBorder(clustersProducts, currentProduct) {
    let minDistance = undefined;

    for (const clusterId in clustersProducts) {
      const clusterRectangle = clustersProducts[clusterId].cluster.find(`.${CONFIG.rectangle.nodeName}`)[0].getClientRect();
      const products = clustersProducts[clusterId].products;

      for (let i = 0; i < products.length; i++) {
        if (products[i] === currentProduct) {
          continue;
        }

        const productRectangle = products[i].getClientRect();

        const distanceToLeftBorder = productRectangle.x - clusterRectangle.x;
        const distanceToRightBorder = (clusterRectangle.x + clusterRectangle.width) - (productRectangle.x + productRectangle.width);
        
        minDistance = minDistance === undefined
          ? Math.min(distanceToLeftBorder, distanceToRightBorder)
          : Math.min(minDistance, Math.min(distanceToLeftBorder, distanceToRightBorder));
      }
    }

    return Math.round(minDistance);
  };

  getVerticalDistanceBetweenProducts(rectangle1, rectangle2) {
    return rectangle1.y > rectangle2.y
      ? Math.round(rectangle1.y - (rectangle2.y + rectangle2.height))
      : Math.round(rectangle2.y - (rectangle1.y + rectangle1.height));
  };

  getHorizontalDistanceBetweenProducts(rectangle1, rectangle2) {
    return rectangle1.x > rectangle2.x
      ? rectangle1.x - (rectangle2.x + rectangle2.width)
      : rectangle2.x - (rectangle1.x + rectangle1.width);
  };

  getProductCluster(clustersProducts, productNode) {
    for (const clusterId in clustersProducts) {
      const cluster = clustersProducts[clusterId].cluster;
      const clusterRectangle = cluster.getClientRect();

      const productRectangle = productNode.getClientRect();

      const isProductInsideCluster = productRectangle.x >= clusterRectangle.x
        && productRectangle.x + productRectangle.width <= clusterRectangle.x + clusterRectangle.width
        && productRectangle.y >= clusterRectangle.y
        && productRectangle.y + productRectangle.height <= clusterRectangle.y + clusterRectangle.height;

      if (isProductInsideCluster) {
        return cluster;
      }
    };

    return null;
  };

  getClusterProducts(clustersProducts, cluster) {
    for (const clusterId in clustersProducts) {
      if (clustersProducts[clusterId].cluster === cluster) {
        return clustersProducts[clusterId].products;
      }
    };

    return null;
  };

  isVerticalIntersection(rect1, rect2) {
    return rect1.y >= rect2.y && rect1.y <= rect2.y + rect2.height
     || rect1.y + rect1.height >= rect2.y && rect1.y + rect1.height <= rect2.y + rect2.height
     || rect2.y >= rect1.y && rect2.y <= rect1.y + rect1.height
     || rect2.y + rect2.height >= rect1.y && rect2.y + rect2.height <= rect1.y + rect1.height;
  };

  isHorizontalIntersection(rect1, rect2) {
    return rect1.x >= rect2.x && rect1.x <= rect2.x + rect2.width
      || rect1.x + rect1.width >= rect2.x && rect1.x + rect1.width <= rect2.x + rect2.width
      || rect2.x >= rect1.x && rect2.x <= rect1.x + rect1.width
      || rect2.x + rect2.width >= rect1.x && rect2.x + rect2.width <= rect1.x + rect1.width;
  };
}

export default new CanvasProductPositioningHelper();